/**
 * The Advertising hub cut-over (PR 5; spec _overhaul-spec/pr05-cutover.md):
 * the old PPC paths redirect into /ads, keeping every filter and scope.
 *
 * Pure, no React. The whole cut-over hangs on ONE constant and ONE table, so
 * reverting this PR (or flipping `ADS_HUB_CUTOVER` to false) brings the old
 * pages, sidebar paths and bottom tabs back exactly as PR 4 left them. Links
 * built straight from adsHref()/adsOpenHref() (alerts, Ctrl+K, row links)
 * point at /ads either way: /ads still mounts with the flag off, so a flag
 * flip leaves those working; only a git revert of the PR restores them to
 * the old paths.
 */
import { ADS_PATH, LEGACY_PATH, adsHref, type AdsTab, type NegKind, type TargetsKind } from './routing'

/** The cut-over switch. true: the old PPC paths redirect into /ads, the
 *  sidebar's single Advertising item opens the hub (PR 6; it replaced the six
 *  PPC entries), links default to the hub mapping and saved bottom tabs migrate. false: everything PR 4 shipped beside
 *  the old pages, exactly as before — the revert path. */
export const ADS_HUB_CUTOVER = true

export type LegacyAdsRoute = {
  path: string            // the old pathname, exact ('/targets'), trailing slash tolerated
  tab: AdsTab             // the hub level it lands on
  /** The hub param the old page's own ?tab= becomes (Targets: kind, Negatives: neg). */
  innerKey?: 'kind' | 'neg'
  /** Params the redirect always sets (the /keywords alias). */
  fixed?: Partial<Record<'kind' | 'neg', TargetsKind | NegKind>>
}

export const LEGACY_ADS_ROUTES: readonly LegacyAdsRoute[] = [
  { path: '/campaigns',     tab: 'campaigns' },
  { path: '/ad-groups',     tab: 'ad-groups' },
  { path: '/targets',       tab: 'targets',  innerKey: 'kind' },   // old ?tab=all|keywords|asins
  { path: '/keywords',      tab: 'targets',  fixed: { kind: 'keywords' } },
  { path: '/search-terms',  tab: 'search-terms' },
  { path: '/placements',    tab: 'placements' },
  { path: '/negatives',     tab: 'negatives', innerKey: 'neg' },   // old ?tab=keywords|products|lists
  { path: '/budget-pacing', tab: 'campaigns' },                     // was → /campaigns
]

/** Old paths that are not hub levels: each one lands on a real page, query kept.
 *  `/ads/overview` was never a ClickPay route (Ads overview lives under Reports);
 *  old links and bottom tabs still carry it. */
export const LEGACY_PATH_REDIRECTS: readonly { path: string; to: string }[] = [
  { path: '/ads/overview', to: '/reports/ads-overview' },
]

/** The old ?tab= values that name a non-default inner tab. 'all' and
 *  'keywords' (Negatives) are the hub's defaults, so they are dropped. */
const INNER_VALUES: Record<'kind' | 'neg', ReadonlySet<string>> = {
  kind: new Set(['keywords', 'asins']),
  neg: new Set(['products', 'lists']),
}

/** The hub URL an old PPC URL becomes, or null when `pathname` is not an old
 *  PPC page (/campaigns/new is a real screen, not a level). Every param rides
 *  along untouched — scope ids and names, include_inactive, <table>_f column
 *  filters, the target_id alias — except the old page's own `tab`, which
 *  becomes the hub's inner-tab param (a default or unknown value is dropped),
 *  and `tab` is written first so the URL reads like adsHref()'s. */
export function legacyAdsHref(pathname: string, search: string): string | null {
  // Route matching is case-insensitive, so the lookup must be too.
  const bare = (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname).toLowerCase()
  const other = LEGACY_PATH_REDIRECTS.find(r => r.path === bare)
  if (other) return `${other.to}${search}`
  const row = LEGACY_ADS_ROUTES.find(r => r.path === bare)
  if (!row) return null
  const out = new URLSearchParams()
  out.set('tab', row.tab)
  for (const [k, v] of Object.entries(row.fixed ?? {})) out.set(k, v)
  const rest = new URLSearchParams()
  for (const [k, v] of new URLSearchParams(search)) {
    if (k !== 'tab') { rest.append(k, v); continue }
    if (row.innerKey && !row.fixed?.[row.innerKey] && INNER_VALUES[row.innerKey].has(v)) out.set(row.innerKey, v)
  }
  for (const [k, v] of rest) out.append(k, v)
  return `${ADS_PATH}?${out.toString()}`
}

/** Where a link to a PPC level goes: the hub's tab under the cut-over, the
 *  old page otherwise. (The sidebar no longer has an entry per level - PR 6's
 *  Advertising item opens the hub - so this serves the in-page links.) */
export function adsEntryPath(tab: AdsTab, cutover: boolean = ADS_HUB_CUTOVER): string {
  return cutover ? adsHref(tab, {}) : LEGACY_PATH[tab]
}
